import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Poppins, Spacer, TextOverflowTooltip } from 'src/common';
import { Tile, TileDescription, TileDescriptionContainer, TileTitle } from 'src/components/Tile';
import { ScenarioSvg } from 'src/assets/dynamicSvg';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { ScenarioGroupSummary } from 'src/graphql';
import CommonMenu from 'src/components/CommonMenu';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import cydeaLogo from 'src/assets/cydea.svg';
import { formatDate } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface ScenarioGroupCardProps {
  group: ScenarioGroupSummary;
}

export const ScenarioGroupCard: React.FC<ScenarioGroupCardProps> = ({ group }) => {
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const scenarioCount = group.scenarios?.length ?? 0;
  const lastEditedAt = group.scenarios.reduce<string | null>((latest, scenario) => {
    const candidate = scenario.meta?.updatedAt || scenario.meta?.createdAt || null;
    if (!candidate) return latest;
    if (!latest) return candidate;
    return new Date(candidate) > new Date(latest) ? candidate : latest;
  }, null);
  const isDisabled = scenarioCount === 0;
  const [anchorEl, setAnchorEl] = React.useState<React.MouseEvent['currentTarget'] | null>(null);
  const textColor = isDisabled ? 'cflowerBlue' : undefined;
  const authorLogo = (group.authorLogoPath || '').trim();
  const logoSrc = authorLogo
    ? authorLogo === 'cydea'
      ? cydeaLogo
      : authorLogo
    : group.isSystem && group.author === 'Cydea'
      ? cydeaLogo
      : group.id === 'COMMON-GRP'
        ? cydeaLogo
        : null;

  const goToGroup = () => {
    if (isDisabled) return;
    navigate(`/scenario-library/groups/${group.id}`);
    mpEvent(MPEvents.Link, {
      source: { value: ['Scenario Library'] },
      destination: { value: ['Scenario Library'], params: { id: group.id } },
      tags: ['SCENARIO'],
    });
  };

  return (
    <>
      <CommonMenu
        onViewClick={(e) => {
          e.stopPropagation();
          setAnchorEl(null);
          goToGroup();
        }}
        anchorEl={isDisabled ? null : anchorEl}
        open={!isDisabled && Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      />
      <Tile
        className="hover-shadow"
        $disabled={isDisabled}
        onClick={() => {
          if (!anchorEl) goToGroup();
        }}
        data-cy="scenario-group-card"
      >
        <div css="display: flex;justify-content: space-between;align-items: center;">
          <TextOverflowTooltip tooltipText={group.name || ''}>
            <Link
              to={`/scenario-library/groups/${group.id}`}
              onClick={(e) => {
                e.stopPropagation();
                goToGroup();
              }}
            >
              <TileTitle color={textColor}>{group.name}</TileTitle>
            </Link>
          </TextOverflowTooltip>
          {!isDisabled && (
            <ThreeDots
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
            />
          )}
        </div>
        <Spacer $px={10} />
        <TileDescriptionContainer>
          <TileDescription>
            {group.description && (
              <Poppins px={14} css="display: block;" color={textColor}>
                {group.description}
              </Poppins>
            )}
          </TileDescription>
        </TileDescriptionContainer>
        {isPlatformAdmin && (
          <>
            <Spacer $px={8} />
            <Poppins px={12} color={textColor} css="opacity: 0.8;">
              Last edited:{' '}
              {formatDate(lastEditedAt, {
                formatType: 'datetime',
                emptyValue: '-',
              })}
            </Poppins>
          </>
        )}
        <Spacer $px={8} />
        <Footer>
          <div css="display: flex;align-items: center;flex: 1;">
            <ScenarioSvg />
            <div>
              <Poppins css="margin-left: 6px;" px={14} color={textColor}>
                {group.category ? `${group.category} • ` : ''}
              </Poppins>
              <Poppins px={14} weight={500} color={textColor}>
                {scenarioCount}{' '}
              </Poppins>
              <Poppins px={14} weight={500} color={textColor}>
                {scenarioCount === 1 ? 'scenario' : 'scenarios'}
              </Poppins>
            </div>
          </div>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={group.author || 'Author'}
              style={{ height: 22, opacity: isDisabled ? 0.6 : 1, objectFit: 'contain' }}
            />
          ) : (
            group.author && (
              <Poppins px={14} color={textColor} css="text-align: right;">
                {group.author}
              </Poppins>
            )
          )}
        </Footer>
      </Tile>
    </>
  );
};
